import type { Procedure } from '@/types/cgt'

/**
 * Cache key for a fetched procedure journey (z8nrz7ckj9).
 *
 * Journeys are fetched per (patient, template) so that several procedures of the
 * same patient on the same template share one request — that sharing is what
 * makes paging back through the procedures list free.
 *
 * A run with NO patient cannot participate in that sharing. Two MES orders on
 * one template are different runs with different progress, so keying them
 * together would show one order's progress on the other. They are keyed by their
 * own procedure id instead.
 *
 * The endpoint mirrors this: pass the MRN alone to get the shareable answer, or
 * the procedure id alone when there is no patient. Passing neither is an error
 * server-side, which is the case this key shape makes unreachable.
 */
export function journeyCacheKey(
  mrn: string | null,
  procedureTemplateId: string,
  procedureId: string,
): string {
  return mrn ? `${mrn}__${procedureTemplateId}` : `proc__${procedureId}`
}

export type JourneySubject = Pick<Procedure, 'id' | 'patientMrn' | 'procedureTemplateId'>

/** One journey fetch: its shared cache key and the arguments the endpoint takes. */
export interface JourneyRequest {
  key: string
  procedureTemplateId: string
  patientMrn: string | null
  /** Only for a patient-less run, where the procedure id IS the scope. */
  procedureId: string | undefined
}

/**
 * The distinct journey fetches a set of procedure rows needs.
 *
 * Several runs of one patient on one template share a key (see
 * `journeyCacheKey`), so they cost one request, not one each. With a patient
 * the procedure id is left off: that is what makes the answer shareable. A
 * patient-less run (MES, z8nrz7ckj9) passes its procedure id instead, because
 * the endpoint takes that as a complete scope.
 */
export function journeyRequests(procedures: readonly JourneySubject[]): JourneyRequest[] {
  const byKey = new Map<string, JourneyRequest>()
  for (const p of procedures) {
    const key = journeyCacheKey(p.patientMrn, p.procedureTemplateId, p.id)
    if (byKey.has(key)) continue
    byKey.set(key, {
      key,
      procedureTemplateId: p.procedureTemplateId,
      patientMrn: p.patientMrn,
      procedureId: p.patientMrn ? undefined : p.id,
    })
  }
  return [...byKey.values()]
}
